/**
 * ห่อ ts-fsrs บางๆ — แปลง Progress (JSON-safe) ↔ Card ของ ts-fsrs
 * ไม่ผูก UI ไม่ผูก deck
 */
import {
  fsrs,
  generatorParameters,
  createEmptyCard,
  State,
  type Card as FSRSCard,
  type Grade,
  type FSRSParameters,
} from 'ts-fsrs'
import type { CardId, DeckId, Progress, Rating } from './types'
import { CardState } from './types'

/** ทวนแล้วจำได้ 90% — ค่ามาตรฐาน สมดุลระหว่างจำแม่นกับจำนวนครั้งที่ต้องทวน */
export const DEFAULT_RETENTION = 0.9

/** ผิดเกินนี้ = leech · การ์ดเสียที่กินเวลาทุกวัน ต้องแก้ mnemonic ไม่ใช่ทวนต่อ */
export const LEECH_THRESHOLD = 6

const params: FSRSParameters = generatorParameters({
  request_retention: DEFAULT_RETENTION,
  enable_fuzz: true,
  enable_short_term: true,
})
const engine = fsrs(params)

export function newProgress(cardId: CardId, deck: DeckId, now = Date.now()): Progress {
  return toProgress(cardId, deck, createEmptyCard(new Date(now)), now)
}

function toFSRS(p: Progress): FSRSCard {
  const card: FSRSCard = {
    due: new Date(p.due),
    stability: p.stability,
    difficulty: p.difficulty,
    elapsed_days: p.elapsedDays,
    scheduled_days: p.scheduledDays,
    learning_steps: p.learningSteps,
    reps: p.reps,
    lapses: p.lapses,
    state: p.state as unknown as State,
  }
  // last_review เป็น optional — ใส่เฉพาะเมื่อเคยทวนจริง (exactOptionalPropertyTypes)
  return p.lastReview > 0 ? { ...card, last_review: new Date(p.lastReview) } : card
}

function toProgress(cardId: CardId, deck: DeckId, c: FSRSCard, updatedAt: number): Progress {
  return {
    cardId,
    deck,
    due: c.due.getTime(),
    stability: c.stability,
    difficulty: c.difficulty,
    elapsedDays: c.elapsed_days,
    scheduledDays: c.scheduled_days,
    learningSteps: c.learning_steps,
    reps: c.reps,
    lapses: c.lapses,
    state: c.state as unknown as CardState,
    lastReview: c.last_review ? c.last_review.getTime() : 0,
    updatedAt,
  }
}

/** ตอบแล้ว → Progress ใหม่ */
export function schedule(p: Progress, rating: Rating, now = Date.now()): Progress {
  const { card } = engine.next(toFSRS(p), new Date(now), rating as unknown as Grade)
  return toProgress(p.cardId, p.deck, card, now)
}

/** ดูว่าถ้าตอบเรตติ้งนี้จะเลื่อนไปอีกกี่วัน — ใช้โชว์บนปุ่มตอนเฉลย */
export function previewIntervals(p: Progress, now = Date.now()): Record<Rating, number> {
  const card = toFSRS(p)
  const at = new Date(now)
  const out = {} as Record<Rating, number>
  for (const r of [1, 2, 3, 4] as const) {
    const next = engine.next(card, at, r as unknown as Grade).card
    out[r] = Math.max(0, next.due.getTime() - now)
  }
  return out
}

export function isLeech(p: Progress): boolean {
  return p.lapses >= LEECH_THRESHOLD
}

/**
 * ย้ายจาก Leitner box (bushu-drill.html เดิม) → FSRS
 * ประมาณคร่าวๆ พอ FSRS จะปรับเข้าที่เองภายในไม่กี่ครั้ง
 */
export function fromLeitnerBox(
  cardId: CardId,
  deck: DeckId,
  box: number,
  now = Date.now(),
): Progress {
  const p = newProgress(cardId, deck, now)
  if (box <= 0) return p

  const days = [0, 0, 2, 5, 10, 20][Math.min(box, 5)] ?? 0
  const day = 86_400_000

  return {
    ...p,
    state: box === 1 ? CardState.Learning : CardState.Review,
    stability: Math.max(1, days),
    difficulty: 5,
    scheduledDays: days,
    elapsedDays: 0,
    learningSteps: box === 1 ? 1 : 0,
    reps: box,
    // กระจายวันครบกำหนดออกไป ไม่ให้เด้งมาพร้อมกันหมดในวันเดียว
    due: now + days * day,
    lastReview: now,
  }
}
